import * as React from 'react';

import fileDownload from 'js-file-download';

import {useSelector, useDispatch} from 'react-redux';
import useI18n from 'src/hooks/useI18n';

import {
  selectUserInfo,
  selectApplicantInformation,
  selectHasCrmPermission,
} from 'src/selectors/crm';

import {
  toastApi,
  Toast,
  TOAST_SEMANTIC,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {
  BodyMedium,
  SubTitleSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  ButtonTabs,
  ButtonTab,
} from '@spaced-out/ui-design-system/lib/components/ButtonTabs';
import {ButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import ApplicantFeedbacks from './feedback';
import ApplicantScorecards from './scorecard';

import * as reduxApi from 'src/utils/redux-api-v2';

import {getApiPrefix} from 'src/utils/crm';
import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';

import css from './style.css';


const FeedbackAndScorecard = ({
  jobId,
  applicantId,
  reloadCandidateData,
  hideDownloadButton = false,
}: {
  jobId: number;
  applicantId: number;
  reloadCandidateData: () => void;
  hideDownloadButton?: boolean;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();
  const user = useSelector(selectUserInfo);
  const {user: userInfo} = user ?? {};
  const viewCandidateFeedbackTab = selectHasCrmPermission(
    user,
    CRM_PERMISSIONS.VIEW_CANDIDATE_FEEDBACK_TAB,
  );
  const viewCandidateScorecardTab = selectHasCrmPermission(
    user,
    CRM_PERMISSIONS.VIEW_CANDIDATE_SCORECARD_TAB,
  );
  const [selectedTab, setSelectedTab] = React.useState(
    viewCandidateFeedbackTab ? 'feedback' : 'scorecard',
  );
  const applicantDetails = useSelector(selectApplicantInformation);
  const currentApplication = applicantDetails?.applications?.find(
    (app: any) => app?.jobId === jobId,
  );
  const {stepCandidates = []} = currentApplication ?? {};
  const hasFeedbacks = React.useMemo(() => {
    if (!stepCandidates || stepCandidates.length === 0) {
      return false;
    }

    return stepCandidates.some((stepCandidate) => {
      // @ts-expect-error - TS2339 - Property 'interviewReviews' does not exist on type 'ApplicationStepCandidate'. | TS2339 - Property 'assessmentCandidate' does not exist on type 'ApplicationStepCandidate'.
      const {interviewReviews = [], assessmentCandidate} = stepCandidate || {};
      const assesmentReports = assessmentCandidate
        ? [assessmentCandidate.report]
        : [];
      return (
        interviewReviews.length > 0 || assesmentReports.some((report) => report)
      );
    });
  }, [stepCandidates]);

  const handleDownloadFeedback = () => {
    window.open(
      window.encodeURI(
        `${getApiPrefix(
          userInfo?.roleTitle ?? '',
        )}/api/interview-reviews-full?jobId=${jobId}&candidateId=${applicantId}`,
      ),
      '_blank',
    );
  };

  const handleDownloadScorecard = async () => {
    toastApi.show(
      <Toast semantic={TOAST_SEMANTIC.information}>
        <ToastTitle>{`${t(
          'Download_Scorecard_Initiated',
          'Download Scorecard Initiated',
        )}!`}</ToastTitle>
      </Toast>,
    );
    await dispatch(
      reduxApi.post(
        `api/download-scorecard-excel?jobId=${jobId}&candidateId=${applicantId}`,
        {},
        {},
        {
          apiPath: '/trmapi/',
          // responseType: 'arrayBuffer', Note: Swatantra - please check here
        },
      ),
    )
      .then((data) => {
        fileDownload(data, 'scorecard.xlsx');
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.success}>
            <ToastTitle>{`${t(
              'Scorecard_downloaded_successfully',
              'Scorecard downloaded successfully',
            )}!`}</ToastTitle>
          </Toast>,
        );
      })
      .catch(() => {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.danger}>
            <ToastTitle>{`${t(
              'Failed_to_download_Scorecard',
              'Failed to download Scorecard',
            )}!`}</ToastTitle>
          </Toast>,
        );
      });
  };

  return (
    <div className={css.feedbackAndScorecardTabContainer}>
      <div className={css.feedbackAndScorecardTabHeader}>
        {viewCandidateFeedbackTab && viewCandidateScorecardTab ? (
          <ButtonTabs
            onButtonTabSelect={(selectedTabId) => setSelectedTab(selectedTabId)}
            selectedButtonTabId={selectedTab}
          >
            <ButtonTab id="feedback">{t('Feedback', 'Feedback')}</ButtonTab>
            <ButtonTab id="scorecard">{t('Scorecard', 'Scorecard')}</ButtonTab>
          </ButtonTabs>
        ) : viewCandidateFeedbackTab ? (
          <SubTitleSmall>{t('Feedback', 'Feedback')}</SubTitleSmall>
        ) : (
          <SubTitleSmall>{t('Scorecard', 'Scorecard')}</SubTitleSmall>
        )}

        {!hideDownloadButton && hasFeedbacks && (
          <ButtonDropdown
            ariaLabel={t('download', 'download')}
            type="tertiary"
            iconRightName="chevron-down"
            menu={{
              options: [
                {
                  key: 'downloadFeedback',
                  label: t('Download_feedback', 'Download feedback'),
                },
                {
                  key: 'downloadScorecard',
                  label: t('Download_scorecard', 'Download scorecard'),
                },
                {
                  key: 'downloadAll',
                  label: t('Download_all', 'Download all'),
                },
              ],
              classNames: {
                wrapper: css.downloadButtonMenuContainer,
              },
            }}
            onOptionSelect={(option) => {
              if (option.key === 'downloadFeedback') {
                handleDownloadFeedback();
              } else if (option.key === 'downloadScorecard') {
                handleDownloadScorecard();
              } else {
                handleDownloadScorecard();
                handleDownloadFeedback();
              }
            }}
            anchorPosition="bottom-end"
          >
            {t('Download', 'Download')}
          </ButtonDropdown>
        )}
      </div>

      {selectedTab === 'feedback' ? (
        <ApplicantFeedbacks
          jobId={jobId}
          reloadCandidateData={reloadCandidateData}
        />
      ) : selectedTab === 'scorecard' ? (
        <ApplicantScorecards jobId={jobId} />
      ) : null}
    </div>
  );
};

export default FeedbackAndScorecard;
